import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, AlertCircle, RefreshCw } from 'lucide-react';
import { Order } from './typres';
import { orderService } from './orderService';
import { Button } from '@/components/ui/button';
import OrderCard from './OrderCard';

const OrdersSkeleton = () => (
  <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 sm:py-8">
    <div className="mx-auto max-w-3xl animate-pulse space-y-5">
      <div className="h-10 w-56 rounded-2xl bg-foreground/10" />
      {[1, 2, 3].map(i => (
        <div key={i} className="h-28 rounded-[24px] bg-foreground/5" />
      ))}
    </div>
  </div>
);

const OrdersPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.getOrders();
      setOrders(response.orders);
    } catch {
      setError('Не удалось загрузить заказы. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  if (loading) return <OrdersSkeleton />;

  return (
    <div className="min-h-screen overflow-x-hidden bg-background px-4 py-6 text-foreground sm:py-8">
      <div className="mx-auto max-w-3xl">

        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <h1 className="text-3xl font-semibold sm:text-5xl">Заказы</h1>
          {orders.length > 0 && (
            <span className="rounded-full bg-foreground/10 px-3 py-0.5 text-sm text-foreground/50">
              {orders.length}
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 flex items-center gap-3 rounded-[20px] border border-red-500/25 bg-red-500/10 p-4 text-red-600 dark:text-red-300">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span className="flex-1 text-sm">{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchOrders}
              className="rounded-full border border-red-500/25 text-red-600 dark:text-red-400 hover:bg-red-500/10"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Повторить
            </Button>
          </div>
        )}

        {/* Empty */}
        {!error && orders.length === 0 && (
          <div className="rounded-[32px] border border-foreground/10 bg-card py-20 text-center shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-foreground/10 bg-foreground/5">
              <ShoppingBag className="h-8 w-8 text-foreground/25" />
            </div>
            <h2 className="mb-2 text-xl font-semibold text-foreground">Заказов пока нет</h2>
            <p className="mb-8 text-sm text-foreground/40">Оформите первый заказ, и он появится здесь</p>
            <Button asChild className="rounded-full bg-red-600 px-8 hover:bg-red-700">
              <Link to="/">Перейти в каталог</Link>
            </Button>
          </div>
        )}

        {/* List */}
        {orders.length > 0 && (
          <div className="space-y-3">
            {orders.map(order => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default OrdersPage;
